import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const LAST_UPDATED = "March 15, 2025";
const APP_NAME = "CryptoNG";
const COMPANY = "CryptoNG Technologies Ltd.";

const termsSections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: `By accessing or using the ${APP_NAME} mobile application and related services, you agree to be bound by these Terms of Service. If you do not agree to any part of these terms, you may not use our services. These terms apply to all users, visitors, and others who access or use the service.`,
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    content: `You must be at least 18 years of age and a resident of Nigeria to use our services. By using ${APP_NAME}, you represent and warrant that you meet these eligibility requirements. ${COMPANY} reserves the right to refuse service to anyone for any reason at any time.`,
  },
  {
    id: "services",
    title: "3. Description of Services",
    content: `${APP_NAME} provides a peer-to-peer cryptocurrency exchange platform enabling Nigerian users to buy and sell digital assets (including Bitcoin, Ethereum, USDT, and others) using Nigerian Naira (₦). Services include wallet management, fiat deposits/withdrawals, real-time price tracking, and portfolio management.`,
  },
  {
    id: "kyc",
    title: "4. KYC & Identity Verification",
    content: `To comply with Nigerian financial regulations and CBN directives, all users must complete Know Your Customer (KYC) verification. We collect government-issued ID, BVN (Bank Verification Number), and other required documents. Failure to complete KYC may result in restricted access to certain features or transaction limits.`,
  },
  {
    id: "transactions",
    title: "5. Transactions & Fees",
    content: `All cryptocurrency transactions on ${APP_NAME} are final and irreversible once confirmed on the blockchain. Transaction fees are displayed prior to confirmation. ${APP_NAME} charges a platform fee of up to 1.5% per trade. Exchange rates are determined at time of order execution and may differ from displayed indicative rates.`,
  },
  {
    id: "prohibited",
    title: "6. Prohibited Activities",
    content: `You agree not to use ${APP_NAME} for: money laundering or financing of illegal activities, circumventing Nigerian tax obligations, fraudulent transactions or chargebacks, creating multiple accounts, using automated bots or scripts, or any activity that violates applicable Nigerian laws including the EFCC Act, Money Laundering (Prohibition) Act, or CBN regulations.`,
  },
  {
    id: "liability",
    title: "7. Limitation of Liability",
    content: `${COMPANY} shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service. Cryptocurrency investments carry significant risk, including total loss of capital. Past performance does not guarantee future results. We are not responsible for market volatility or losses incurred through trading.`,
  },
  {
    id: "termination",
    title: "8. Account Suspension & Termination",
    content: `We reserve the right to suspend or terminate your account at our sole discretion, with or without notice, for violations of these terms, suspicious activity, or legal/regulatory requirements. Upon termination, you retain rights to withdraw available balances subject to our withdrawal policies and applicable holds.`,
  },
  {
    id: "governing",
    title: "9. Governing Law",
    content: `These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Lagos State, Nigeria. You waive any objection to proceedings in such courts.`,
  },
];

const privacySections = [
  {
    id: "collection",
    title: "1. Information We Collect",
    content: `We collect information you provide directly: full name, email address, phone number, date of birth, BVN, government-issued ID documents, bank account details, and residential address. We also automatically collect device information, IP address, transaction history, login timestamps, and app usage analytics to improve our service.`,
  },
  {
    id: "usage",
    title: "2. How We Use Your Information",
    content: `Your information is used to: verify your identity (KYC compliance), process cryptocurrency and fiat transactions, send transaction notifications and security alerts, comply with Nigerian financial regulations and NDPR requirements, prevent fraud and money laundering, improve our platform, and communicate service updates and promotional offers (with your consent).`,
  },
  {
    id: "sharing",
    title: "3. Information Sharing",
    content: `We do not sell your personal data. We may share your information with: regulatory authorities (CBN, EFCC, NFIU) when legally required, identity verification partners (for KYC processing), payment processors and banking partners, fraud prevention services, and cloud infrastructure providers under strict data processing agreements. All third parties are bound by confidentiality obligations.`,
  },
  {
    id: "security",
    title: "4. Data Security",
    content: `We implement industry-standard security measures including AES-256 encryption for data at rest, TLS 1.3 for data in transit, two-factor authentication, biometric login support, and regular security audits. BVN and sensitive financial data are never stored in plain text. Despite these measures, no transmission over the internet is 100% secure.`,
  },
  {
    id: "retention",
    title: "5. Data Retention",
    content: `We retain your personal data for as long as your account is active and as required by Nigerian financial regulations (typically 5–7 years after account closure per CBN guidelines). Transaction records are kept for a minimum of 7 years. You may request deletion of non-regulatory data by contacting our support team.`,
  },
  {
    id: "rights",
    title: "6. Your Rights (NDPR)",
    content: `Under the Nigeria Data Protection Regulation (NDPR), you have the right to: access your personal data, correct inaccurate information, request data deletion (subject to legal retention requirements), object to certain processing, request data portability, and withdraw consent. To exercise these rights, contact our Data Protection Officer at privacy@cryptong.com.`,
  },
  {
    id: "cookies",
    title: "7. Cookies & Tracking",
    content: `Our mobile app uses local storage and analytics SDKs (including Firebase Analytics) to improve user experience and understand usage patterns. We do not use third-party advertising cookies. Analytics data is anonymised and aggregated. You may opt out of analytics tracking in app settings without affecting core functionality.`,
  },
  {
    id: "children",
    title: "8. Children's Privacy",
    content: `${APP_NAME} is not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors. If we become aware that a minor has provided personal information, we will take steps to delete such information promptly and terminate the associated account.`,
  },
  {
    id: "updates",
    title: "9. Policy Updates",
    content: `We may update this Privacy Policy periodically to reflect changes in our practices or applicable law. We will notify you of material changes via email or in-app notification at least 14 days before they take effect. Continued use of ${APP_NAME} after the effective date constitutes acceptance of the updated policy.`,
  },
  {
    id: "contact",
    title: "10. Contact Us",
    content: `For privacy-related enquiries, contact our Data Protection Officer: Email: privacy@cryptong.com | Phone: +234 800 000 0000 | Address: ${COMPANY}, 12 Marina Street, Lagos Island, Lagos State, Nigeria. We respond to all verified requests within 14 business days.`,
  },
];

export default function TermsPrivacy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "terms");
  const [expandedSection, setExpandedSection] = useState(null);

  // Sync tab from URL param: /legal?tab=privacy
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "privacy" || tab === "terms") setActiveTab(tab);
  }, [searchParams]);

  const sections = activeTab === "terms" ? termsSections : privacySections;

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-4 border-b border-slate-100 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0"
          >
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg">Legal Documents</h1>
            <p className="text-slate-400 text-xs">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-slate-100 rounded-2xl p-1 gap-1">
          {[
            { key: "terms", label: "Terms of Service", icon: "ri-file-text-line" },
            { key: "privacy", label: "Privacy Policy", icon: "ri-shield-user-line" },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setExpandedSection(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                activeTab === key
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500"
              }`}
            >
              <i className={icon} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary banner */}
      <div className="px-5 mt-4">
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className={`text-emerald-600 text-lg ${activeTab === "terms" ? "ri-scales-3-line" : "ri-lock-line"}`} />
          </div>
          <div>
            <p className="text-emerald-800 text-sm font-bold">
              {activeTab === "terms" ? "Terms of Service" : "Privacy Policy"}
            </p>
            <p className="text-emerald-600 text-xs mt-0.5 leading-relaxed">
              {activeTab === "terms"
                ? `These terms govern your use of ${APP_NAME}. By using our services you agree to these terms.`
                : "We are committed to protecting your data in compliance with NDPR and Nigerian law."}
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="px-5 mt-4">
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden divide-y divide-slate-50">
          {sections.map((section) => {
            const isExpanded = expandedSection === section.id;
            return (
              <div key={section.id}>
                <button
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <p className="text-slate-700 text-sm font-semibold pr-4 leading-snug">
                    {section.title}
                  </p>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isExpanded ? "bg-emerald-100" : "bg-slate-100"
                  }`}>
                    <i className={`text-sm transition-all ${
                      isExpanded ? "ri-subtract-line text-emerald-600" : "ri-add-line text-slate-500"
                    }`} />
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-5 pb-5">
                    <p className="text-slate-500 text-sm leading-relaxed">{section.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 mt-6 text-center space-y-1.5">
        <p className="text-slate-400 text-xs">
          Questions?{" "}
          <a href="mailto:legal@cryptong.com" className="text-emerald-600 font-semibold">
            legal@cryptong.com
          </a>
        </p>
        <p className="text-slate-300 text-xs">
          &copy; {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
